import { useMemo } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useSession } from '@sdk/session/SessionContext';
import isOrgFeatureEnabled from '@sdk/authentication/helpers/isOrgFeatureEnabled';
import { Feature } from '@sdk/common/features';

import { ConversationsRootPage } from '@web/conversations/ConversationsRootPage/ConversationsRootPage';
import { ConversationView } from '@web/conversations/ConversationView/ConversationView';
import { NewConversationScreen } from '@web/conversations/NewConversationScreen/NewConversationScreen';
import { CreateGroupConversation } from '@web/conversations/CreateGroupConversation/CreateGroupConversation';
import { CreatePrivateConversation } from '@web/conversations/CreatePrivateConversation/CreatePrivateConversation';
import { ConversationRoute } from '@web/conversations/Conversation/ConversationRoute/ConversationsRoute';
import { SearchAcrossConversations } from '@web/conversations/ConversationSearch/SearchAcrossConversations/SearchAcrossConversations';
import { ConversationEdit } from '@web/conversations/ConversationEdit/ConversationEdit';
import { FeaturePreview } from '@web/conversations/FeaturePreview/FeaturePreview';
import { StartConversation } from '@web/conversations/StartConversation/StartConversation';

import { PathnameEnum } from './router.types';

export const Router = () => {
  const { currentUser } = useSession();
  const isMessagesModuleEnabled =
    isOrgFeatureEnabled(Feature.groupMessages, currentUser) ||
    isOrgFeatureEnabled(Feature.privateMessages, currentUser);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: PathnameEnum.ROOT,
          // @FIXME angular $locationProvider resolves new path,
          // before window.location is updated, hence even if it
          // seems to be `/messages` route, it actually is still root - `/`.
          // You can check it with `console.log(window.location.pathname)`.
          // This small fix solves the issue, however may be obsolete in the future
          element: <Navigate to={PathnameEnum.CONVERSATION_ROOT} replace />,
        },
        {
          // @INFO this wildcard path, will prevent react from warning
          // about undefined routes, and let the angular to handle those.
          // This will make angular to handle /404 page as well.
          path: PathnameEnum.SPLAT,
          element: null,
        },
        ...(!isMessagesModuleEnabled
          ? [
              {
                path: PathnameEnum.CONVERSATION_FEATURE_PREVIEW,
                element: (
                  <FeaturePreview
                    withPremiumGuard={currentUser.isAdmin || currentUser.isManager}
                    feature={Feature.messages}
                    currentUser={currentUser}
                  />
                ),
              },
              {
                path: PathnameEnum.CONVERSATION_SPLAT,
                element: <Navigate to={PathnameEnum.CONVERSATION_FEATURE_PREVIEW} />,
              },
            ]
          : [
              {
                path: PathnameEnum.CONVERSATION_ROOT,
                element: <ConversationsRootPage />,
                children: [
                  {
                    element: <ConversationView />,
                    children: [
                      {
                        index: true,
                        element: <ConversationRoute />,
                      },
                      {
                        path: PathnameEnum.CONVERSATION_DETAILS,
                        element: <ConversationRoute />,
                      },
                      {
                        path: PathnameEnum.CONVERSATION_SEARCH,
                        element: <SearchAcrossConversations />,
                      },
                    ],
                  },
                  {
                    path: PathnameEnum.CONVERSATION_EDIT,
                    element: <ConversationEdit />,
                  },
                  {
                    path: PathnameEnum.CONVERSATION_COMPOSE,
                    children: [
                      {
                        index: true,
                        element: <NewConversationScreen />,
                      },
                      {
                        path: PathnameEnum.CONVERSATION_COMPOSE_GROUP,
                        element: <CreateGroupConversation />,
                      },
                      {
                        path: PathnameEnum.CONVERSATION_COMPOSE_PRIVATE,
                        element: <CreatePrivateConversation />,
                      },
                    ],
                  },
                  {
                    path: PathnameEnum.CONVERSATION_START,
                    element: <StartConversation />,
                  },
                ],
              },
              {
                path: PathnameEnum.CONVERSATION_SPLAT,
                element: <Navigate replace to={PathnameEnum.ERROR_404} />,
              },
            ]),
      ]),
    [currentUser, isMessagesModuleEnabled]
  );

  return <RouterProvider router={router} />;
};
